import { Anchor, Button, Dropdown, Flex, P, Tabs } from '@dnb/eufemia';
import { launch as LaunchIcon } from '@dnb/eufemia/icons';
import { TrackRoute } from '@portals/shared-frontend/components';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';

import HeroPage from '@/components/HeroPage';
import { HeroContent } from '@/components/HeroPage/HeroContent';
import HeroTopContent from '@/components/HeroPage/HeroTopContent';
import { getEventCatalogBaseUrl } from '@/constants';
import { useBackend } from '@/hooks/useBackend';
import { useUser } from '@/hooks/useUser';
import { EventEnvironmentResponse, EventReleaseResponse } from '@/openapi/schema';
import { DropdownOption } from '@/pages/api-documentation/constants/types';

import EventResourceNotFoundError from './EventResourceNotFoundError';
import KafkaConfigView from './KafkaConfigView/KafkaConfigView';
import SchemasView from './SchemasView';

export default function EventPage() {
  const navigate = useNavigate();
  const { api: slug, environment: environmentParam, version: versionParam, tab: tabParam } = useParams();
  const { user } = useUser();

  const [selectedEnvironment, setSelectedEnvironment] = useState<EventEnvironmentResponse>();
  const [activeTab, setActiveTab] = useState(tabParam);
  const [selectedRelease, setSelectedRelease] = useState<DropdownOption>();
  const [allEnvironments, setAllEnvironments] = useState<DropdownOption[]>();
  const [releaseDropdownOptions, setReleaseDropdownOptions] = useState<DropdownOption[]>();

  const { data: event, isValidating: isValidatingSlug } = useBackend(
    `/events/{eventSlug}`,
    slug ? { params: { path: { eventSlug: slug } } } : null,
  );

  const { data: envs, isValidating: isValidatingEnvs } = useBackend(
    `/events/{eventSlug}/environments`,
    slug ? { params: { path: { eventSlug: slug } } } : null,
  );

  const { data: releases, isValidating: isValidatingReleases } = useBackend(
    `/events/{eventSlug}/environments/{environmentSlug}/releases`,
    slug && selectedEnvironment
      ? {
          params: {
            path: {
              eventSlug: slug,
              environmentSlug: selectedEnvironment.slug,
            },
          },
        }
      : null,
  );

  useEffect(() => {
    if (!isValidatingEnvs && envs) {
      const defaultEnvironment = envs.find((env: EventEnvironmentResponse) =>
        environmentParam === '@default' ? env.isDefault : env.slug === environmentParam,
      );
      const allEnvironments = envs?.map((env) => ({
        content: env.name,
        selectedKey: env.slug,
      }));
      setSelectedEnvironment(defaultEnvironment ?? envs[0]);
      setAllEnvironments(allEnvironments);
    }
  }, [environmentParam, envs, isValidatingEnvs]);

  useEffect(() => {
    if (releases) {
      const options: DropdownOption[] = [];
      releases.sort(
        (a: EventReleaseResponse, b: EventReleaseResponse) =>
          new Date(b.deployedAt || '1970-01-01').getTime() - new Date(a.deployedAt || '1970-01-01').getTime(),
      );

      releases.forEach((release: EventReleaseResponse) => {
        options.push({
          content: release.version,
          selectedValue: release?.version,
          selectedKey: release.version,
        });
      });

      setReleaseDropdownOptions(options);
      setSelectedRelease(
        versionParam === '@latest' ? options[0] : options.find((option) => option.content === versionParam),
      );
    }
  }, [releases, versionParam]);

  const isLoading = isValidatingSlug || isValidatingEnvs || isValidatingReleases;

  //Change this after react 19 upgrade to use the useHistory hook
  const changeUrlAndState = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (data: any, state: 'env' | 'ver' | 'tab') => {
      const urlIndexToChange = state === 'env' ? 3 : state === 'ver' ? 4 : 5;
      const url: string[] = globalThis.location.pathname.toString().split('/');
      let valueToChange;
      if (state === 'env') {
        valueToChange = data.selectedKey;
        setSelectedEnvironment(undefined);
        setSelectedRelease(undefined);
        url[4] = '@latest';
      }
      if (state === 'ver') {
        valueToChange = data.selectedValue;
      }
      if (state === 'tab') {
        setActiveTab(data);
        valueToChange = data;
      }
      url[urlIndexToChange] = valueToChange;
      const combinedUrl = url.join('/');
      navigate(combinedUrl);
    },
    [navigate],
  );

  const isUserOwner = useMemo(
    () => Boolean(event?.ownerTeamId && user?.teams.some((team) => team.id === event.ownerTeamId)),
    [event, user?.teams],
  );

  const eventCatalogBaseUrl = selectedEnvironment?.slug ? getEventCatalogBaseUrl(selectedEnvironment.slug) : null;

  const eventCatalogUrl = event && eventCatalogBaseUrl ? `${eventCatalogBaseUrl}/docs/events/${event.slug}/` : null;

  const releaseDropdown = useMemo(
    () => (
      <Flex.Stack gap={false}>
        <P>Version</P>
        <Dropdown
          data={releaseDropdownOptions}
          onChange={({ data }) => changeUrlAndState(data, 'ver')}
          skeleton={isValidatingReleases}
          stretch
          title="Releases"
          value={releases?.findIndex((option) => option.version === selectedRelease?.selectedKey)}
        />
      </Flex.Stack>
    ),
    [changeUrlAndState, isValidatingReleases, releaseDropdownOptions, releases, selectedRelease?.selectedKey],
  );

  const schemaContent = useMemo(
    () =>
      selectedEnvironment && event && selectedRelease ? (
        <SchemasView
          environmentSlug={selectedEnvironment.slug}
          eventSlug={event.slug}
          releaseVersion={selectedRelease.selectedKey}
          topChild={releaseDropdown}
        />
      ) : (
        !isLoading && (
          <HeroContent>
            <EventResourceNotFoundError type="release" />
          </HeroContent>
        )
      ),
    [selectedEnvironment, event, selectedRelease, releaseDropdown, isLoading],
  );

  const kafkaContent = useMemo(
    () =>
      selectedEnvironment &&
      event && <KafkaConfigView environmentSlug={selectedEnvironment.slug} eventSlug={event.slug} />,
    [selectedEnvironment, event],
  );

  const tabsData = useMemo(
    () =>
      [
        { title: 'Schemas', key: 'schemas' },
        { title: 'Kafka config', key: 'kafka' },
        eventCatalogUrl && {
          key: 'event-catalog',
          title: (
            <Anchor href={eventCatalogUrl} noUnderline target="_blank">
              Event Catalog
            </Anchor>
          ),
        },
      ].filter(Boolean),
    [eventCatalogUrl],
  );

  return (
    <HeroPage banner="documentation" noTopMargin subtitle={event?.description} title={event?.name}>
      {event && (
        <TrackRoute name={event.name} path={`/events/${event.slug}/@default/@latest/schema`} type="event-doc" />
      )}
      <HeroTopContent noBottomPadding>
        <div className="flex justify-between items-end">
          <Dropdown
            data={allEnvironments}
            label="Environment"
            labelDirection="vertical"
            onChange={({ data }) => changeUrlAndState(data, 'env')}
            skeleton={isValidatingEnvs}
            title="Environments"
            value={allEnvironments?.findIndex((option) => option.selectedKey === selectedEnvironment?.slug)}
          />
          {isUserOwner && event && (
            <Button
              element={RouterLink}
              icon={<LaunchIcon />}
              target="_blank"
              to={`/team/${event?.ownerTeamId}/application/${event.appId}/api/${event.id}`}
              tooltip="Manage visibility, access and more."
              top="small"
              variant="secondary"
            >
              Manage event
            </Button>
          )}
        </div>
        <Tabs
          contentInnerSpace={false}
          data={tabsData}
          id="SchemaSwitcher"
          onChange={({ selectedKey }) => changeUrlAndState(selectedKey, 'tab')}
          onClick={({ selectedKey }) => {
            return selectedKey !== 'event-catalog';
          }}
          selectedKey={activeTab}
          top="small"
        />
      </HeroTopContent>
      {activeTab === 'schemas' && schemaContent}
      {activeTab === 'kafka' && kafkaContent}
    </HeroPage>
  );
}
